"use client";

import type { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { isOfficeAddin } from "@/lib/host";
import { useHost } from "@/lib/host-context";
import { acquireToken, initMsal } from "@/lib/msal-browser";
import { getOfficeLoginHint } from "@/lib/office";
import { setOfficeAddinSessionToken } from "@/lib/office-addin-session";
import { useOffice } from "./office-provider";

/** Dev-only debug logging. Tree-shaken in production. */
const DEV = process.env.NODE_ENV !== "production";
type NestedMsalAuthState = {
  isInitializing: boolean;
  isAuthenticated: boolean;
  account: AccountInfo | null;
  accessToken: string | null;
  error: string | null;
  login: () => Promise<void>;
};
const NestedMsalAuthContext = createContext<NestedMsalAuthState>({
  isInitializing: true,
  isAuthenticated: false,
  account: null,
  accessToken: null,
  error: null,
  // biome-ignore lint/suspicious/noEmptyBlockStatements: default no-op
  login: async () => {}
});
export function useNestedMsalAuth() {
  return useContext(NestedMsalAuthContext);
}

/**
 * Manages MSAL Nested App Auth for the Office Add-in.
 *
 * On mount (after Office.onReady), initialises MSAL and attempts a silent
 * token acquisition.  If the user is signed into Office, NAA should succeed
 * without any prompts.  Exposes login() for manual retry.
 */
export function NestedMsalAuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    isReady: officeReady
  } = useOffice();
  const host = useHost();
  // This provider is only mounted inside HostProvider's office-addin
  // branch, so `host` is always an OfficeAddinSurface at runtime. The
  // narrow gives us the typed value for the MSAL registry; the throw is
  // defensive in case the tree is ever wired up incorrectly.
  if (!isOfficeAddin(host)) {
    throw new Error(`NestedMsalAuthProvider mounted on non-add-in surface: ${host}`);
  }
  const surface = host;
  const [state, setState] = useState<Omit<NestedMsalAuthState, "login">>({
    isInitializing: true,
    isAuthenticated: false,
    account: null,
    accessToken: null,
    error: null
  });
  const attemptAuth = useCallback(async () => {
    setState(prev => ({
      ...prev,
      isInitializing: true,
      error: null
    }));
    try {
      if (DEV) {
        console.info("[Magnus Add-in] Starting MSAL initialization", {
          surface
        });
      }
      await initMsal(surface);
      const loginHint = await getOfficeLoginHint();
      if (DEV) {
        console.info("[Magnus Add-in] Attempting token acquisition", {
          hasLoginHint: !!loginHint,
          surface
        });
      }
      const result: AuthenticationResult = await acquireToken(surface, undefined, loginHint);

      // Exchange the MSAL token for a Magnus add-in session
      // (`magnus-addin-session-token` cookie + Bearer token in
      // sessionStorage) so subsequent API / tRPC calls are authenticated.
      // See ADR-044 §6 for why we don't reuse Better Auth's signed cookie.
      if (DEV) {
        console.info("[Magnus Add-in] Exchanging token for session");
      }
      const exchangeRes = await fetch("/api/auth/app-exchange", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          accessToken: result.accessToken,
          surface
        })
      });
      if (!exchangeRes.ok) {
        const body = await exchangeRes.json().catch(() => ({}));
        throw new Error((body as {
          error?: string;
        }).error ?? `Token exchange failed (${exchangeRes.status})`);
      }

      // Save session token to sessionStorage so we can inject it as a
      // Bearer header on every fetch.  WebView2 silently drops secure
      // cookies on localhost, so this is the only reliable way.
      const exchangeData = await exchangeRes.json();
      if (exchangeData.sessionToken) {
        setOfficeAddinSessionToken(exchangeData.sessionToken);
      } else {
        console.warn("[Magnus Add-in] Exchange response missing sessionToken");
      }

      // Diagnostic-only: in dev, round-trip the layered `getSession()`
      // endpoint to confirm the Bearer token resolves to a session row.
      // Skipped in production — the AddinAuthGate already runs the same
      // check via its own session-verify call.
      if (DEV) {
        const {
          getOfficeAddinSessionHeaders
        } = await import("@/lib/office-addin-session");
        const sessionVerify = await fetch("/api/auth/office-addin-verify", {
          headers: {
            ...getOfficeAddinSessionHeaders()
          }
        });
        const sessionData = await sessionVerify.json().catch(() => null);
        console.info("[Magnus Add-in] Session verify after exchange:", {
          status: sessionVerify.status,
          hasSession: !!sessionData?.session,
          userId: sessionData?.session?.userId ?? null
        });
      }
      setState({
        isInitializing: false,
        isAuthenticated: true,
        account: result.account,
        accessToken: result.accessToken,
        error: null
      });
      if (DEV) {
        console.info("[Magnus Add-in] Authentication successful", {
          username: result.account?.username
        });
      }
    } catch (err) {
      // Detect user-cancelled / dismissed popup — not a fatal error, just
      // show the sign-in screen so the user can retry.
      const isCancelled = err instanceof Error && /user.*(cancel|close|denied|dismiss)|BrowserAuthError.*user_cancelled/i.test(err.message);
      const message = isCancelled ? "Sign-in was cancelled. Click below to try again." : err instanceof Error ? err.message : "Authentication failed";
      if (isCancelled) {
        if (DEV) {
          console.info("[Magnus Add-in] User cancelled auth flow");
        }
      } else {
        // Real authentication failure — keep visible in production so
        // observability catches token-exchange / Graph / DB issues.
        console.error("[Magnus Add-in] Authentication error:", err);
      }
      setState({
        isInitializing: false,
        isAuthenticated: false,
        account: null,
        accessToken: null,
        error: message
      });
    }
  }, [surface]);
  useEffect(() => {
    if (officeReady) {
      attemptAuth();
    }
  }, [officeReady, attemptAuth]);
  const value: NestedMsalAuthState = {
    ...state,
    login: attemptAuth
  };
  return <NestedMsalAuthContext.Provider value={value}>
      {children}
    </NestedMsalAuthContext.Provider>;
}